import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { catchError, of, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QaiCallInsightsCardComponent, QaiProgressBarComponent, QaiSegmentedBarGraphKind } from '@qai/ui';
import { TranslocoService } from '@jsverse/transloco';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { IObjectivesStats } from 'src/app/interfaces/iobjective-stats';
import { getKindByPercentage } from 'src/app/utils/score-kind.util';
import { SimulationDetailListModalComponent } from 'src/app/pages/reporting-dashboard/components/modals/simulation-detail-list-modal/simulation-detail-list-modal.component';

@Component({
  selector: 'app-methodology-averages',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    QaiCallInsightsCardComponent,
    QaiProgressBarComponent,
    SimulationDetailListModalComponent,
  ],
  templateUrl: './methodology-averages.component.html',
  styleUrls: ['./methodology-averages.component.scss'],
})
export class MethodologyAveragesComponent {
  readonly moduleAssignmentUuid = input<string>('');
  readonly simulationUuid = input<string>('');
  readonly accountUuid = input<string>('');
  readonly teamUuids = input<string[]>([]);

  readonly methodologyScaleMax = 5;
  metrics = signal<IObjectivesStats[]>([]);

  readonly modalSubheading = computed(() =>
    this.translocoService.translate('MODAL_SUBHEADING', { count: this.metrics().length }, 'reportingV2')
  );

  readonly objectiveFooterLabel = computed(() => {
    const count = this.metrics().length;
    return count > 5
      ? this.translocoService.translate('VIEW_ALL_OBJECTIVES', { count }, 'reportingV2')
      : undefined;
  });

  @ViewChild('listModal') private listModalRef!: SimulationDetailListModalComponent;

  readonly showPercentage = false;
  readonly showValueMax = true;
  readonly noMetricsMessage = this.translocoService.translate(
    'NO_METRICS_MESSAGE',
    {},
    'reportingV2',
  );

  constructor(
    private readonly reportingDashboardService: ReportingDashboardService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translocoService: TranslocoService,
    private readonly destroyRef: DestroyRef,
  ) {
    effect(() => {
      this.loadData();
    });
  }

  get heading(): string {
    return this.translocoService.translate('METHODOLOGY', {}, 'reportingV2');
  }

  get description(): { content: string } {
    return {
      content: this.translocoService.translate(
        'METHODOLOGY_SCORE_DESCRIPTION',
        {},
        'reportingV2',
      ),
    };
  }

  private loadData(): void {
    const accountUuid = this.accountUuid();
    const moduleAssignmentUuid = this.moduleAssignmentUuid();
    const simulationUuid = this.simulationUuid();
    const teamUuids = this.teamUuids() ?? [];

    if (!accountUuid || !moduleAssignmentUuid || !simulationUuid) {
      this.metrics.set([]);
      console.log(
        'Invalid accountUuid, moduleAssignmentUuid, or simulationUuid',
      );
      return;
    }

    this.reportingDashboardService
      .getAverageObjectiveScores({
        accountUuid,
        moduleAssignmentUuid,
        simulationUuid,
        teamUuids,
      }, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((items) => {
          this.metrics.set(items);
          this.cdr.markForCheck();
        }),
        catchError((err) => {
          console.error('MethodologyAveragesComponent: load failed', err);
          this.metrics.set([]);
          this.cdr.markForCheck();
          return of([]);
        }),
      )
      .subscribe();
  }

  readonly displayedMetrics = computed(() =>
    [...this.metrics()]
      .sort((a, b) => a.averageScore - b.averageScore)
      .slice(0, 5)
  );

  onViewAllObjectives(): void {
    this.listModalRef?.open();
  }

  getKindByPercentage(percentage: number): QaiSegmentedBarGraphKind {
    return getKindByPercentage(percentage);
  }
}
